import { useState } from 'react'
import { Search, Users, ChevronDown, ChevronRight, Mail, Briefcase, MapPin, Phone, Building2, Linkedin, Globe, Trash2, ChevronUp } from 'lucide-react'
import toast from 'react-hot-toast'

export default function ContactsList({ contacts, isLoading, searchQuery, onSearchChange, onDeleteContact }) {
  const [expandedContacts, setExpandedContacts] = useState(new Set())
  const [isListCollapsed, setIsListCollapsed] = useState(false)

  const toggleContact = (contactId) => {
    const newExpanded = new Set(expandedContacts)
    if (newExpanded.has(contactId)) {
      newExpanded.delete(contactId)
    } else {
      newExpanded.add(contactId)
    }
    setExpandedContacts(newExpanded)
  }

  const handleDelete = async (contactId, contactEmail) => {
    if (!confirm(`Supprimer ${contactEmail} ?`)) {
      return
    }
    
    try {
      await onDeleteContact(contactId, contactEmail)
      toast.success('Contact supprimé')
    } catch (error) {
      toast.error('Erreur lors de la suppression')
    }
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
        <div className="spinning" style={{ width: 48, height: 48, border: '4px solid rgba(91, 92, 244,0.1)', borderTop: '4px solid #5b5cf4', borderRadius: '50%', margin: '0 auto 1rem', animation: 'spin 1s linear infinite' }}></div>
        Chargement des contacts...
      </div>
    )
  }

  if (contacts.length === 0) {
    return (
      <div>
        {/* Barre de recherche */}
        <div style={{ marginBottom: '2rem', position: 'relative' }}>
          <Search
            size={20}
            style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#888'
            }}
          />
          <input
            type="text"
            placeholder="Rechercher un contact..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '0.75rem 1rem 0.75rem 3rem',
              color: '#fff',
              fontSize: '0.875rem'
            }}
          />
        </div>

        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Users size={48} style={{ color: '#888', marginBottom: '1rem' }} />
          <p style={{ color: '#888', fontSize: '1rem' }}>
            {searchQuery ? 'Aucun contact trouvé' : 'Aucun contact disponible'}
          </p>
          <p style={{ color: '#666', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            {!searchQuery && 'Ajoutez des prospects avec des emails pour commencer'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Barre de recherche */}
      <div style={{ marginBottom: '2rem', position: 'relative' }}>
        <Search
          size={20}
          style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#888'
          }}
        />
        <input
          type="text"
          placeholder="Rechercher un contact..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px',
            padding: '0.75rem 1rem 0.75rem 3rem',
            color: '#fff',
            fontSize: '0.875rem',
            transition: 'all 0.2s'
          }}
        />
      </div>

      {/* Liste des contacts */}
      <div style={{
        background: 'rgba(255,255,255,0.02)',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.1)',
        overflow: 'hidden'
      }}>
        {/* Header collapsable */}
        <div
          onClick={() => setIsListCollapsed(!isListCollapsed)}
          style={{
            padding: '1rem 1.5rem',
            background: 'rgba(0,0,0,0.2)',
            borderBottom: isListCollapsed ? 'none' : '1px solid rgba(255,255,255,0.1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.3)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.2)'}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: '#fff'
          }}>
            <Users size={18} style={{ color: '#5b5cf4' }} />
            Liste des contacts ({contacts.length})
          </div>
          <div style={{ 
            color: isListCollapsed ? '#888' : '#5b5cf4',
            display: 'flex',
            alignItems: 'center'
          }}>
            {isListCollapsed ? <ChevronRight size={18} /> : <ChevronUp size={18} />}
          </div>
        </div>

        {/* Contenu collapsable */}
        <div style={{
          maxHeight: isListCollapsed ? '0' : '10000px',
          overflow: 'hidden',
          transition: 'max-height 0.3s ease'
        }}>
        {contacts.map((contact, index) => {
          const isExpanded = expandedContacts.has(contact.id)
          
          return (
            <div
              key={contact.id}
              style={{
                borderBottom: index < contacts.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
              }}
            >
              {/* Contact Header */}
              <div
                onClick={() => toggleContact(contact.id)}
                style={{
                  padding: '1rem 1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  transition: 'background 0.2s',
                  background: isExpanded ? 'rgba(91, 92, 244,0.03)' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'
                }}
                onMouseLeave={(e) => {
                  if (!isExpanded) e.currentTarget.style.background = 'transparent'
                }}
              >
                {/* Chevron */}
                <div style={{ 
                  color: isExpanded ? '#5b5cf4' : '#888',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </div>

                {/* Nom */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontWeight: 600, 
                    color: '#fff',
                    fontSize: '0.9375rem',
                    marginBottom: '0.25rem'
                  }}>
                    {contact.firstName} {contact.lastName}
                    {contact.fullName && !contact.firstName && !contact.lastName && contact.fullName}
                  </div>
                  <div style={{ 
                    color: '#5b5cf4', 
                    fontSize: '0.8125rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Mail size={14} />
                    {contact.primaryEmail}
                  </div>
                </div>

                {/* Entreprise et Poste */}
                <div style={{ 
                  display: 'flex',
                  gap: '2rem',
                  color: '#888',
                  fontSize: '0.875rem'
                }}>
                  {contact.companyName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Building2 size={14} />
                      {contact.companyName}
                    </div>
                  )}
                  {contact.jobTitle && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Briefcase size={14} />
                      {contact.jobTitle}
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Details (Expanded) */}
              <div
                style={{
                  maxHeight: isExpanded ? '1000px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease, opacity 0.3s ease',
                  opacity: isExpanded ? 1 : 0,
                }}
              >
                <div style={{
                  padding: '1.5rem 1.5rem 1.5rem 3.5rem',
                  background: 'rgba(0,0,0,0.2)',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1.5rem'
                }}>
                  {/* Contact Info */}
                  <div>
                    <div style={{ 
                      color: '#888', 
                      fontSize: '0.75rem', 
                      fontWeight: 600, 
                      textTransform: 'uppercase', 
                      marginBottom: '0.75rem',
                      letterSpacing: '0.05em'
                    }}>
                      Informations de contact
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {contact.primaryEmail && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <Mail size={16} style={{ color: '#5b5cf4' }} />
                          <a href={`mailto:${contact.primaryEmail}`} style={{ color: '#5b5cf4', textDecoration: 'none', fontSize: '0.875rem' }}>
                            {contact.primaryEmail}
                          </a>
                        </div>
                      )}
                      {contact.primaryPhone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <Phone size={16} style={{ color: '#888' }} />
                          <span style={{ color: '#fff', fontSize: '0.875rem' }}>{contact.primaryPhone}</span>
                        </div>
                      )}
                      {contact.linkedinProfile && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <Linkedin size={16} style={{ color: '#0077b5' }} />
                          <a href={contact.linkedinProfile} target="_blank" rel="noopener noreferrer" style={{ color: '#0077b5', textDecoration: 'none', fontSize: '0.875rem' }}>
                            Voir le profil
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company Info */}
                  {(contact.companyName || contact.companyWebsite || contact.companyIndustry) && (
                    <div>
                      <div style={{ 
                        color: '#888', 
                        fontSize: '0.75rem', 
                        fontWeight: 600, 
                        textTransform: 'uppercase', 
                        marginBottom: '0.75rem',
                        letterSpacing: '0.05em'
                      }}>
                        Entreprise
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {contact.companyName && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <Building2 size={16} style={{ color: '#888' }} />
                            <span style={{ color: '#fff', fontSize: '0.875rem' }}>{contact.companyName}</span>
                          </div>
                        )}
                        {contact.companyIndustry && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <Briefcase size={16} style={{ color: '#888' }} />
                            <span style={{ color: '#aaa', fontSize: '0.875rem' }}>{contact.companyIndustry}</span>
                          </div>
                        )}
                        {contact.companyWebsite && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <Globe size={16} style={{ color: '#888' }} />
                            <a href={contact.companyWebsite} target="_blank" rel="noopener noreferrer" style={{ color: '#5b5cf4', textDecoration: 'none', fontSize: '0.875rem' }}>
                              {contact.companyWebsite.replace(/^https?:\/\//, '')}
                            </a>
                          </div>
                        )}
                        {contact.companyEmployees && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <Users size={16} style={{ color: '#888' }} />
                            <span style={{ color: '#aaa', fontSize: '0.875rem' }}>{contact.companyEmployees} employés</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Professional Info */}
                  {(contact.jobTitle || contact.seniority || contact.departments || contact.country) && (
                    <div>
                      <div style={{ 
                        color: '#888', 
                        fontSize: '0.75rem', 
                        fontWeight: 600, 
                        textTransform: 'uppercase', 
                        marginBottom: '0.75rem',
                        letterSpacing: '0.05em'
                      }}>
                        Informations professionnelles
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {contact.jobTitle && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <Briefcase size={16} style={{ color: '#888' }} />
                            <span style={{ color: '#fff', fontSize: '0.875rem' }}>{contact.jobTitle}</span>
                          </div>
                        )}
                        {contact.seniority && (
                          <div style={{ color: '#aaa', fontSize: '0.875rem', paddingLeft: '1.75rem' }}>
                            Niveau: {contact.seniority}
                          </div>
                        )}
                        {contact.departments && (
                          <div style={{ color: '#aaa', fontSize: '0.875rem', paddingLeft: '1.75rem' }}>
                            Département: {contact.departments}
                          </div>
                        )}
                        {contact.country && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <MapPin size={16} style={{ color: '#888' }} />
                            <span style={{ color: '#aaa', fontSize: '0.875rem' }}>{contact.country}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {contact.companyDescription && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ 
                        color: '#888', 
                        fontSize: '0.75rem', 
                        fontWeight: 600, 
                        textTransform: 'uppercase', 
                        marginBottom: '0.75rem',
                        letterSpacing: '0.05em'
                      }}>
                        À propos de l'entreprise
                      </div>
                      <p style={{ color: '#aaa', fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                        {contact.companyDescription}
                      </p>
                    </div>
                  )}

                  {/* Bouton supprimer */}
                  <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(contact.id, contact.primaryEmail)
                      }}
                      style={{
                        padding: '0.625rem 1rem',
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        borderRadius: '6px',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)'
                        e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
                        e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)'
                      }}
                    >
                      <Trash2 size={14} />
                      Supprimer ce contact
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
        </div>
      </div>

      {/* Compteur */}
      <div style={{
        marginTop: '1rem',
        textAlign: 'center',
        color: '#666',
        fontSize: '0.875rem'
      }}>
        {contacts.length} contact{contacts.length > 1 ? 's' : ''} affiché{contacts.length > 1 ? 's' : ''}
      </div>
    </div>
  )
}
